import { useCallback, useState } from "react";
import { COMPILATION_MAX_RANGE_DAYS, COMPILATION_MAX_RANGE_MS } from "../../../client-messages/compilation";
import { isValidTime, pendingCompilationRequest, zonedInstant, type PendingCompilationRequest } from "./date-range";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseRequestCompilationFormOptions {
  /**
   * Receives the resolved request — the camera and the absolute window the
   * entered dates and times denote in the camera's timezone. Handing over
   * instants rather than the raw fields is what stops a caller re-reading the
   * same wall clock in its own zone and requesting a window other than the one
   * the form showed and counted.
   */
  readonly onSubmit: (request: PendingCompilationRequest) => void;
  readonly loading?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * The timezone the chosen camera sits in, which is what the entered dates
   * and times mean. Called with the selected camera; returning `undefined`
   * (nothing selected yet, or a site with no timezone recorded) falls back to
   * the device's own zone.
   *
   * A callback rather than a plain timezone because the camera is selected
   * inside this hook — a caller cannot resolve the zone before the hook that
   * owns the selection has run.
   */
  readonly timezoneFor?: ((cameraId: string) => string | undefined) | undefined;
}

export interface FormField<T> {
  readonly value: T;
  readonly error: string | undefined;
  readonly onChange: (value: T) => void;
}

export interface UseRequestCompilationFormReturn {
  /**
   * The timezone the form is reading its dates and times in — the selected
   * camera's, or the device's while no camera is selected. Returned so the
   * form displays the same zone it resolves against.
   */
  readonly timezone: string;
  /**
   * The request a submit would send, or `null` while the selection is
   * incomplete or invalid. Exposed so a caller can ask about the pending
   * selection — the clip count in the dialog is fetched for exactly this
   * window — without re-deriving (and risking disagreeing about) validity.
   */
  readonly pendingRequest: PendingCompilationRequest | null;
  readonly camera: FormField<string | null>;
  readonly startDate: FormField<Date | null>;
  readonly startTime: FormField<string>;
  readonly endDate: FormField<Date | null>;
  readonly endTime: FormField<string>;
  readonly canSubmit: boolean;
  readonly onSubmit: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** The device's own IANA zone, used until a camera names a better one. */
function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function maskTimeInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRequestCompilationForm({
  onSubmit,
  loading,
  disabled,
  timezoneFor,
}: UseRequestCompilationFormOptions): UseRequestCompilationFormReturn {
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState("23:59");

  const handleStartTimeChange = useCallback((text: string) => {
    setStartTime(maskTimeInput(text));
  }, []);

  const handleEndTimeChange = useCallback((text: string) => {
    setEndTime(maskTimeInput(text));
  }, []);

  const timezone = (cameraId === null ? undefined : timezoneFor?.(cameraId)) ?? deviceTimezone();

  const startTimeError =
    startTime.length > 0 && startTime.length === 5 && !isValidTime(startTime) ? "Invalid time" : undefined;

  const isInvertedRange =
    startDate !== null &&
    endDate !== null &&
    isValidTime(startTime) &&
    isValidTime(endTime) &&
    startDate.getTime() === endDate.getTime() &&
    startTime >= endTime;

  // Mirrors the backend's own refine check on `RequestCompilationBody` (see
  // `client-messages/compilation.ts`) — the date pickers' min/max props keep
  // most out-of-range selections from being made in the first place, but
  // time-of-day alone can still push a same-day-span combination over the
  // limit, so this re-checks the exact combined duration.
  const rangeExceedsMax =
    startDate !== null &&
    endDate !== null &&
    isValidTime(startTime) &&
    isValidTime(endTime) &&
    zonedInstant(endDate, endTime, timezone).getTime() - zonedInstant(startDate, startTime, timezone).getTime() >
      COMPILATION_MAX_RANGE_MS;

  const endTimeError =
    endTime.length > 0 && endTime.length === 5 && !isValidTime(endTime) ? "Invalid time"
    : isInvertedRange ? "Must be after start time"
    : rangeExceedsMax ? `Range must be ${String(COMPILATION_MAX_RANGE_DAYS)} days or less`
    : undefined;

  // Every bound the backend enforces is checked in one place — see
  // `pendingCompilationRequest`. The field-level errors above stay separate
  // because they have to say *which* bound was missed.
  const pendingRequest = pendingCompilationRequest({ cameraId, startDate, startTime, endDate, endTime, timezone });

  const canSubmit = pendingRequest !== null && !disabled && !loading;

  const handleSubmit = useCallback(() => {
    if (!canSubmit || pendingRequest === null) return;
    onSubmit(pendingRequest);
  }, [canSubmit, onSubmit, pendingRequest]);

  return {
    timezone,
    pendingRequest,
    camera: { value: cameraId, error: undefined, onChange: setCameraId },
    startDate: { value: startDate, error: undefined, onChange: setStartDate },
    startTime: { value: startTime, error: startTimeError, onChange: handleStartTimeChange },
    endDate: { value: endDate, error: undefined, onChange: setEndDate },
    endTime: { value: endTime, error: endTimeError, onChange: handleEndTimeChange },
    canSubmit,
    onSubmit: handleSubmit,
  };
}
